import React, { useEffect, useState } from "react";
import StaffNavBar from "../components/StaffNavBar";
import {
  AdminPageBody,
  AdminPanel,
  AdminLoadingState,
  adminButton,
  adminPrimaryButton,
} from "../components/AdminPageShell";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faReceipt, faRotate, faXmark } from "@fortawesome/free-solid-svg-icons";
import { useStaffExamPurchases } from "../store/staff";

const inputCls =
  "w-full rounded-lg border border-slate-300 bg-white px-3 py-2 text-sm text-slate-800 outline-none focus:border-[#16213A] focus:ring-2 focus:ring-[#16213A]/15";

const PAYMENT_STATUSES = ["PENDING", "COMPLETED", "FAILED", "ABANDONED"];
const FULFILLMENT_STATUSES = [
  "FULFILLED",
  "PENDING_FULFILLMENT",
  "INVENTORY_SHORTAGE",
  "PENDING_PAYMENT",
  "NOT_FULFILLED",
];
const EMAIL_STATUSES = ["SENT", "FAILED", "QUEUED", "NONE"];

const money = (value) =>
  value === undefined || value === null || Number.isNaN(Number(value))
    ? "—"
    : `₦${Number(value).toLocaleString()}`;

const fmtDate = (value) => (value ? new Date(value).toLocaleString() : "—");

const badge = (value) =>
  value ? (
    <span className="rounded-full bg-slate-100 px-2.5 py-0.5 text-xs font-semibold text-slate-600">
      {value}
    </span>
  ) : (
    "—"
  );

const StaffExamPurchases = () => {
  const {
    staffExamPurchases,
    staffExamPurchasesLoading,
    staffExamPurchasesError,
    staffExamPurchasesTotal,
    staffExamPurchasesPages,
    staffExamPurchaseStats,
    staffExamPurchaseDetail,
    staffExamPurchaseDetailLoading,
    staffExamPurchaseMutationLoading,
    staffExamPurchaseMutationError,
    fetchStaffExamPurchases,
    fetchStaffExamPurchase,
    fetchStaffExamPurchaseStats,
    retryStaffExamPurchaseFulfillment,
  } = useStaffExamPurchases();

  const [page, setPage] = useState(1);
  const [search, setSearch] = useState("");
  const [paymentStatus, setPaymentStatus] = useState("");
  const [fulfillmentStatus, setFulfillmentStatus] = useState("");
  const [emailStatus, setEmailStatus] = useState("");
  const [detailId, setDetailId] = useState(null);

  const params = {
    page,
    limit: 10,
    ...(search ? { search } : {}),
    ...(paymentStatus ? { paymentStatus } : {}),
    ...(fulfillmentStatus ? { fulfillmentStatus } : {}),
    ...(emailStatus ? { emailStatus } : {}),
  };

  // GET /api/staff/exam-purchases (+ stats)
  useEffect(() => {
    fetchStaffExamPurchases(params);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [page, search, paymentStatus, fulfillmentStatus, emailStatus]);

  useEffect(() => {
    fetchStaffExamPurchaseStats();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const reload = () => {
    fetchStaffExamPurchases(params);
    fetchStaffExamPurchaseStats();
  };

  // GET /api/staff/exam-purchases/stats
  const stats = staffExamPurchaseStats || {};
  const payments = stats.payments || {};
  const fulfillmentStats = stats.fulfillment || {};
  const emailStats = stats.emails || {};
  const statCards = [
    { label: "Total purchases", value: stats.totalPurchases },
    { label: "Payments completed", value: payments.completed },
    { label: "Fulfilled", value: fulfillmentStats.fulfilled },
    { label: "Failed emails", value: emailStats.failed },
  ];

  const detail = staffExamPurchaseDetail || {};
  const purchaser = detail.purchaser || {};
  const payment = detail.payment || {};
  const orderFulfillment = detail.fulfillment || {};
  const orderEmail = detail.email || {};
  const timeline = Array.isArray(detail.timeline) ? detail.timeline : [];
  const activity = Array.isArray(detail.activity) ? detail.activity : [];

  const canRetry = (purchase) =>
    purchase?.status === "COMPLETED" &&
    purchase?.fulfillment?.status !== "FULFILLED";

  return (
    <div>
      <StaffNavBar
        title="Exam Purchases"
        context="Track Exam PIN purchases, fulfillment and email delivery."
      />
      <AdminPageBody>
        <div className="grid grid-cols-2 gap-4 lg:grid-cols-4">
          {statCards.map((s) => (
            <AdminPanel key={s.label} className="p-5">
              <div className="mb-1 text-2xl font-bold text-slate-900">
                {s.value != null ? Number(s.value).toLocaleString() : "—"}
              </div>
              <div className="text-sm text-slate-500">{s.label}</div>
            </AdminPanel>
          ))}
        </div>

        <AdminPanel className="overflow-hidden">
          <div className="flex flex-wrap items-center gap-3 border-b border-slate-200 p-4">
            <input
              className={`${inputCls} max-w-xs`}
              placeholder="Search reference / email…"
              value={search}
              onChange={(e) => {
                setPage(1);
                setSearch(e.target.value);
              }}
            />
            <select
              className={`${inputCls} max-w-[190px]`}
              value={paymentStatus}
              onChange={(e) => {
                setPage(1);
                setPaymentStatus(e.target.value);
              }}
            >
              <option value="">All payments</option>
              {PAYMENT_STATUSES.map((value) => (
                <option key={value}>{value}</option>
              ))}
            </select>
            <select
              className={`${inputCls} max-w-[210px]`}
              value={fulfillmentStatus}
              onChange={(e) => {
                setPage(1);
                setFulfillmentStatus(e.target.value);
              }}
            >
              <option value="">All fulfillment</option>
              {FULFILLMENT_STATUSES.map((value) => (
                <option key={value}>{value}</option>
              ))}
            </select>
            <select
              className={`${inputCls} max-w-[170px]`}
              value={emailStatus}
              onChange={(e) => {
                setPage(1);
                setEmailStatus(e.target.value);
              }}
            >
              <option value="">All emails</option>
              {EMAIL_STATUSES.map((value) => (
                <option key={value}>{value}</option>
              ))}
            </select>
            <button className={`${adminButton} ml-auto`} onClick={reload}>
              <FontAwesomeIcon icon={faRotate} /> Refresh
            </button>
          </div>

          {staffExamPurchaseMutationError && (
            <div className="border-b border-red-100 bg-red-50 px-4 py-2 text-sm text-red-700">
              {staffExamPurchaseMutationError}
            </div>
          )}
          {staffExamPurchasesError && (
            <div className="border-b border-red-100 bg-red-50 px-4 py-2 text-sm text-red-700">
              {staffExamPurchasesError}
            </div>
          )}

          {staffExamPurchasesLoading ? (
            <AdminLoadingState />
          ) : staffExamPurchases.length === 0 ? (
            <div className="px-4 py-12 text-center text-sm text-slate-400">
              No purchases found.
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-left text-sm">
                <thead className="bg-slate-50 text-xs uppercase tracking-wide text-slate-500">
                  <tr>
                    <th className="px-5 py-3 font-semibold">Reference</th>
                    <th className="px-5 py-3 font-semibold">Purchaser</th>
                    <th className="px-5 py-3 font-semibold">Amount</th>
                    <th className="px-5 py-3 font-semibold">Payment</th>
                    <th className="px-5 py-3 font-semibold">Fulfillment</th>
                    <th className="px-5 py-3 font-semibold">Email</th>
                    <th className="px-5 py-3 text-center font-semibold">Actions</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-100">
                  {staffExamPurchases.map((p) => (
                    <tr key={p.id} className="hover:bg-slate-50">
                      <td className="px-5 py-3 font-mono text-slate-800">
                        {p.reference || p.id}
                      </td>
                      <td className="px-5 py-3 text-slate-600">
                        <div className="font-medium text-slate-800">
                          {p.purchaser?.name || p.purchaser?.email || "—"}
                        </div>
                        <div className="text-xs text-slate-400">
                          {p.purchaser?.email || "—"}
                        </div>
                      </td>
                      <td className="px-5 py-3 text-slate-700">{money(p.amount)}</td>
                      <td className="px-5 py-3">{badge(p.status)}</td>
                      <td className="px-5 py-3">
                        {badge(p.fulfillment?.status)}
                        {p.fulfillment?.maskedPin && (
                          <div className="mt-1 font-mono text-[11px] text-slate-400">
                            {p.fulfillment.maskedPin}
                          </div>
                        )}
                      </td>
                      <td className="px-5 py-3">{badge(p.email?.status)}</td>
                      <td className="px-5 py-3">
                        <div className="flex items-center justify-center gap-2">
                          <button
                            className={adminButton}
                            onClick={() => {
                              setDetailId(p.id);
                              fetchStaffExamPurchase(p.id);
                            }}
                          >
                            View
                          </button>
                          <button
                            className={adminButton}
                            title="Retry fulfillment"
                            disabled={
                              staffExamPurchaseMutationLoading || !canRetry(p)
                            }
                            onClick={async () => {
                              const res = await retryStaffExamPurchaseFulfillment(p.id);
                              if (res.success) reload();
                            }}
                          >
                            <FontAwesomeIcon icon={faRotate} />
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}

          <div className="flex items-center justify-between border-t border-slate-200 p-4 text-sm text-slate-500">
            <span>
              Page {page} of {staffExamPurchasesPages || 1} · {staffExamPurchasesTotal || 0} purchases
            </span>
            <div className="flex gap-2">
              <button className={adminButton} disabled={page <= 1} onClick={() => setPage((p) => p - 1)}>
                Prev
              </button>
              <button
                className={adminButton}
                disabled={page >= (staffExamPurchasesPages || 1)}
                onClick={() => setPage((p) => p + 1)}
              >
                Next
              </button>
            </div>
          </div>
        </AdminPanel>
      </AdminPageBody>

      {detailId && (
        <div className="fixed inset-0 z-50 flex items-center justify-center overflow-y-auto bg-black/50 p-4">
          <div className="my-auto w-full max-w-3xl rounded-2xl bg-white p-6 shadow-2xl">
            <div className="mb-4 flex items-center justify-between">
              <h3 className="m-0 flex items-center gap-2 text-lg font-semibold text-slate-900">
                <FontAwesomeIcon icon={faReceipt} /> Purchase Details
              </h3>
              <button
                className="text-slate-400 hover:text-slate-700"
                onClick={() => setDetailId(null)}
              >
                <FontAwesomeIcon icon={faXmark} />
              </button>
            </div>

            {staffExamPurchaseDetailLoading ? (
              <AdminLoadingState />
            ) : (
              <div className="space-y-5">
                <dl className="grid grid-cols-1 gap-x-6 gap-y-3 sm:grid-cols-3">
                  {[
                    ["Reference", detail.reference],
                    ["Amount", money(detail.amount)],
                    ["Currency", detail.currency || "NGN"],
                    ["Status", detail.status],
                    ["Created", fmtDate(detail.createdAt)],
                    ["Updated", fmtDate(detail.updatedAt)],
                  ].map(([label, value]) => (
                    <div key={label}>
                      <dt className="text-xs font-semibold uppercase tracking-wide text-slate-400">
                        {label}
                      </dt>
                      <dd className="m-0 text-sm text-slate-800">
                        {value != null && value !== "" ? String(value) : "—"}
                      </dd>
                    </div>
                  ))}
                </dl>

                <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
                  <section className="rounded-lg border border-slate-200 p-4">
                    <h4 className="mb-3 mt-0 text-sm font-semibold text-slate-900">Purchaser</h4>
                    <p className="mb-1 text-sm text-slate-800">{purchaser.name || "—"}</p>
                    <p className="mb-1 text-sm text-slate-600">{purchaser.email || "—"}</p>
                    <p className="m-0 text-sm text-slate-600">{purchaser.phone || "—"}</p>
                  </section>

                  <section className="rounded-lg border border-slate-200 p-4">
                    <h4 className="mb-3 mt-0 text-sm font-semibold text-slate-900">Payment</h4>
                    <dl className="grid grid-cols-2 gap-y-2 text-sm">
                      <dt className="text-slate-500">Status</dt>
                      <dd className="m-0 text-right text-slate-800">{payment.status || "—"}</dd>
                      <dt className="text-slate-500">Reference</dt>
                      <dd className="m-0 break-all text-right text-slate-800">
                        {payment.reference || "—"}
                      </dd>
                      <dt className="text-slate-500">Gateway ref</dt>
                      <dd className="m-0 break-all text-right text-slate-800">
                        {payment.gatewayReference || "—"}
                      </dd>
                      <dt className="text-slate-500">Amount</dt>
                      <dd className="m-0 text-right text-slate-800">{money(payment.amount)}</dd>
                      <dt className="text-slate-500">Mismatch</dt>
                      <dd className="m-0 text-right text-slate-800">
                        {payment.amountMismatch ? "Yes" : "No"}
                      </dd>
                      <dt className="text-slate-500">Paid</dt>
                      <dd className="m-0 text-right text-slate-800">{fmtDate(payment.paidAt)}</dd>
                      <dt className="text-slate-500">Verified</dt>
                      <dd className="m-0 text-right text-slate-800">
                        {fmtDate(payment.verifiedAt)}
                      </dd>
                    </dl>
                  </section>

                  <section className="rounded-lg border border-slate-200 p-4">
                    <h4 className="mb-3 mt-0 text-sm font-semibold text-slate-900">Fulfillment</h4>
                    <dl className="grid grid-cols-2 gap-y-2 text-sm">
                      <dt className="text-slate-500">Status</dt>
                      <dd className="m-0 text-right text-slate-800">
                        {orderFulfillment.status || "—"}
                      </dd>
                      <dt className="text-slate-500">PIN</dt>
                      <dd className="m-0 text-right font-mono text-slate-800">
                        {orderFulfillment.maskedPin || "—"}
                      </dd>
                      <dt className="text-slate-500">Sold at</dt>
                      <dd className="m-0 text-right text-slate-800">
                        {fmtDate(orderFulfillment.soldAt)}
                      </dd>
                    </dl>
                  </section>

                  <section className="rounded-lg border border-slate-200 p-4">
                    <h4 className="mb-3 mt-0 text-sm font-semibold text-slate-900">Email</h4>
                    <dl className="grid grid-cols-2 gap-y-2 text-sm">
                      <dt className="text-slate-500">Status</dt>
                      <dd className="m-0 text-right text-slate-800">{orderEmail.status || "—"}</dd>
                      <dt className="text-slate-500">Provider</dt>
                      <dd className="m-0 text-right text-slate-800">
                        {orderEmail.provider || "—"}
                      </dd>
                      <dt className="text-slate-500">Attempts</dt>
                      <dd className="m-0 text-right text-slate-800">
                        {orderEmail.attempts ?? "—"}
                      </dd>
                      <dt className="text-slate-500">Sent</dt>
                      <dd className="m-0 text-right text-slate-800">
                        {fmtDate(orderEmail.sentAt)}
                      </dd>
                    </dl>
                    {orderEmail.lastError && (
                      <p className="mb-0 mt-2 break-words text-xs text-red-700">
                        {orderEmail.lastError}
                      </p>
                    )}
                  </section>
                </div>

                <section>
                  <h4 className="mb-2 text-sm font-semibold text-slate-900">Timeline</h4>
                  {timeline.length === 0 ? (
                    <p className="text-sm text-slate-400">No timeline events.</p>
                  ) : (
                    <ul className="divide-y divide-slate-100 rounded-lg border border-slate-200">
                      {timeline.map((event, index) => (
                        <li
                          key={`${event.type}-${event.timestamp}-${index}`}
                          className="flex items-start justify-between gap-3 px-4 py-2.5"
                        >
                          <div>
                            <div className="text-sm font-medium text-slate-800">
                              {event.type || "—"}
                            </div>
                            {(event.maskedPin || event.provider) && (
                              <div className="text-xs text-slate-500">
                                {[event.maskedPin, event.provider].filter(Boolean).join(" · ")}
                              </div>
                            )}
                            {event.lastError && (
                              <div className="text-xs text-red-700">{event.lastError}</div>
                            )}
                          </div>
                          <span className="shrink-0 text-xs text-slate-400">
                            {fmtDate(event.timestamp)}
                          </span>
                        </li>
                      ))}
                    </ul>
                  )}
                </section>

                <section>
                  <h4 className="mb-2 text-sm font-semibold text-slate-900">Activity</h4>
                  {activity.length === 0 ? (
                    <p className="text-sm text-slate-400">No activity recorded.</p>
                  ) : (
                    <ul className="divide-y divide-slate-100 rounded-lg border border-slate-200">
                      {activity.map((entry, index) => (
                        <li key={`${entry.createdAt}-${index}`} className="px-4 py-2.5">
                          <div className="flex items-center justify-between gap-3">
                            <span className="text-sm font-medium text-slate-800">
                              {entry.action || "—"}
                            </span>
                            <span className="text-xs text-slate-400">
                              {fmtDate(entry.createdAt)}
                            </span>
                          </div>
                          <div className="text-xs text-slate-500">
                            {[entry.actorType, entry.actorId].filter(Boolean).join(" · ") || "—"}
                          </div>
                        </li>
                      ))}
                    </ul>
                  )}
                </section>
              </div>
            )}

            <div className="mt-5 flex justify-end gap-2">
              <button
                className={adminButton}
                disabled={
                  staffExamPurchaseMutationLoading ||
                  !canRetry({
                    status: detail.status,
                    fulfillment: detail.fulfillment,
                  })
                }
                onClick={async () => {
                  const res = await retryStaffExamPurchaseFulfillment(detailId);
                  if (res.success) {
                    await fetchStaffExamPurchase(detailId);
                    await reload();
                  }
                }}
              >
                <FontAwesomeIcon icon={faRotate} /> Retry fulfillment
              </button>
              <button className={adminPrimaryButton} onClick={() => setDetailId(null)}>
                Close
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default StaffExamPurchases;
